import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, ExternalLink, ChevronRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { SellerAddressFixer } from "@/components/SellerAddressFixer";
import {
  SELLER_COMPLIANCE_CATEGORIES,
  type SellerComplianceCategory,
  type ComplianceResource,
} from "@shared/compliance-resources";

interface ComplianceStatus {
  hasAttestation: boolean;
  isCurrent: boolean;
  currentVersion: string;
  attestedVersion?: string;
  state?: string;
  categories?: string[];
  attestedAt?: string;
}

interface ComplianceResourcesResponse {
  resources: ComplianceResource[];
  stateRecognized: boolean;
  advisoryMessage?: string;
  currentVersion: string;
}

export default function SellerCompliance() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const sellerState = String((user as any)?.state ?? "").trim();
  const [selectedCategories, setSelectedCategories] = useState<SellerComplianceCategory[]>([]);
  const [ackReadSources, setAckReadSources] = useState(false);
  const [ackComplianceLaws, setAckComplianceLaws] = useState(false);
  const [ackPermitsObtained, setAckPermitsObtained] = useState(false);
  const [ackStoopNotLiable, setAckStoopNotLiable] = useState(false);
  const [ackTerms, setAckTerms] = useState(false);
  const [ackRedMeat, setAckRedMeat] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data: status } = useQuery<ComplianceStatus>({
    queryKey: ["/api/seller/compliance/status"],
    queryFn: async () => {
      const res = await fetch("/api/seller/compliance/status", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load compliance status");
      return res.json();
    },
  });

  const categoriesParam = selectedCategories.join(",");
  const { data: resourcesData, isLoading: resourcesLoading } = useQuery<ComplianceResourcesResponse>({
    queryKey: ["/api/seller/compliance/resources", sellerState, categoriesParam],
    queryFn: async () => {
      const params = new URLSearchParams({ state: sellerState, categories: categoriesParam });
      const res = await fetch(`/api/seller/compliance/resources?${params}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load compliance resources");
      return res.json();
    },
    enabled: selectedCategories.length > 0 && Boolean(sellerState),
  });

  const sellsMeat = selectedCategories.includes("meat_poultry");
  const allAcked =
    ackReadSources && ackComplianceLaws && ackPermitsObtained && ackStoopNotLiable && ackTerms &&
    (!sellsMeat || ackRedMeat);
  const canSubmit = selectedCategories.length > 0 && allAcked && Boolean(sellerState);

  const toggleCategory = (cat: SellerComplianceCategory) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/seller/compliance/attest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          state: sellerState,
          categories: selectedCategories,
          ackReadSources,
          ackComplianceLaws,
          ackPermitsObtained,
          ackStoopNotLiable,
          ackRedMeat: sellsMeat ? ackRedMeat : undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message ?? "Submission failed");
      }
      queryClient.invalidateQueries({ queryKey: ["/api/seller/compliance/status"] });
      // Attesting flips any compliance-pending listings live server-side;
      // refresh the seller's list so their statuses update (staleTime: Infinity
      // means it won't refetch on its own).
      queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] });
      toast({
        title: "Attestation recorded",
        description: "Your listings can now be purchased.",
      });
      setLocation("/seller");
    } catch (err: any) {
      toast({
        title: "Couldn't save attestation",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Pre-populate from prior attestation if seller is re-attesting after a
  // version bump (lets them confirm rather than re-pick from scratch).
  useEffect(() => {
    if (!status?.hasAttestation || status.isCurrent) return;
    const priorCategories = (status.categories ?? []) as SellerComplianceCategory[];
    if (priorCategories.length > 0 && selectedCategories.length === 0) {
      setSelectedCategories(priorCategories);
    }
  }, [status?.hasAttestation, status?.isCurrent, status?.categories, selectedCategories.length]);

  const stateMissing = !sellerState;

  if (status?.isCurrent) {
    return (
      <div className="container mx-auto max-w-2xl px-4 py-6">
        <div className="rounded-2xl border border-brand-secondary/30 bg-brand-secondary/5 p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-secondary" />
            <div>
              <h2 className="font-semibold text-foreground">
                Compliance attestation on file
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                You attested to compliance for {(status.categories ?? []).join(", ")} in{" "}
                {status.state} on{" "}
                {status.attestedAt
                  ? new Date(status.attestedAt).toLocaleDateString()
                  : "—"}
                . Version {status.attestedVersion}.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setLocation("/seller")}
              >
                Back to dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-2xl px-4 py-6 space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-foreground">
          Before you publish
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Selling food, drink, or homemade goods to neighbors is regulated at the
          state level. Stoop is not your legal advisor — you're responsible for
          knowing and following the laws that apply to what you sell. This step
          captures that you've reviewed the relevant rules and accept that
          responsibility.
        </p>
      </div>

      <button
        type="button"
        onClick={() => setLocation("/seller/meat-guidance")}
        className="flex w-full items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 text-left transition hover:border-primary/50"
      >
        <span className="min-w-0">
          <span className="block font-medium text-foreground">
            Selling meat or poultry? Read this first
          </span>
          <span className="mt-0.5 block text-sm text-muted-foreground">
            What USDA inspection is required, and how to legally sell a share of
            a live animal.
          </span>
        </span>
        <ChevronRight className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
      </button>

      {stateMissing && (
        <div className="rounded-2xl border border-primary/20 bg-card p-4 sm:p-5">
          <SellerAddressFixer
            title="Add your address to continue"
            description="We use your state to show the right compliance rules for what you sell. Buyers also see how close you are (your exact address is only shared after they check out)."
            onSaved={() => {
              // Refresh the auth user so user.state (sellerState) updates and
              // this gate clears right here — no trip out to the settings menu.
              queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
            }}
          />
        </div>
      )}

      {!stateMissing && (
        <>
          <section className="space-y-3">
            <h2 className="font-semibold text-foreground">
              What will you be selling?
            </h2>
            <p className="text-sm text-muted-foreground">
              Select all categories that apply. We'll show you resources tailored
              to those.
            </p>
            <div className="grid gap-2">
              {SELLER_COMPLIANCE_CATEGORIES.map((cat) => {
                const checked = selectedCategories.includes(cat.value);
                return (
                  <label
                    key={cat.value}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                      checked
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCategory(cat.value)}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-foreground">{cat.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {cat.examples}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </section>

          {selectedCategories.length > 0 && (
            <section className="space-y-3">
              <h2 className="font-semibold text-foreground">
                Review these resources for {sellerState}
              </h2>
              {resourcesLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading resources…
                </div>
              )}
              {resourcesData && !resourcesData.stateRecognized && resourcesData.advisoryMessage && (
                <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-3 text-sm text-foreground">
                  {resourcesData.advisoryMessage}
                </div>
              )}
              <ul className="space-y-3">
                {resourcesData?.resources.map((r) => (
                  <li
                    key={r.url}
                    className="rounded-xl border border-border p-3"
                  >
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                    >
                      {r.title}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {r.description}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {selectedCategories.length > 0 && (
            <section className="space-y-3 rounded-2xl border border-border bg-card p-4">
              <h2 className="font-semibold text-foreground">Acknowledgments</h2>
              <p className="text-sm text-muted-foreground">
                Each line below is a separate statement. Check each box to
                continue.
              </p>
              <div className="space-y-3">
                <label className="flex cursor-pointer items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={ackReadSources}
                    onChange={(e) => setAckReadSources(e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-foreground">
                    I have reviewed the resources above for the categories I plan
                    to sell.
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={ackComplianceLaws}
                    onChange={(e) => setAckComplianceLaws(e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-foreground">
                    I am operating in compliance with applicable state and federal
                    laws for what I'm selling.
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={ackPermitsObtained}
                    onChange={(e) => setAckPermitsObtained(e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-foreground">
                    I have obtained, or will obtain before listing, any permits or
                    licenses my state requires for the categories I'm selling.
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={ackStoopNotLiable}
                    onChange={(e) => setAckStoopNotLiable(e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-foreground">
                    I understand Stoop is not responsible for my compliance with
                    these laws and is not my legal advisor. I accept full
                    responsibility for the products I sell.
                  </span>
                </label>
                {sellsMeat && (
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800/40 dark:bg-amber-950/20">
                    <input
                      type="checkbox"
                      checked={ackRedMeat}
                      onChange={(e) => setAckRedMeat(e.target.checked)}
                      className="mt-1"
                    />
                    <span className="text-foreground">
                      <strong>Red meat (pork, beef, lamb, goat):</strong> I understand it
                      cannot be sold as retail cuts without USDA or state inspection. For any
                      red meat I list, it is either (a) processed at a USDA/state-inspected
                      facility, or (b) sold as a live-animal share (whole/half) with custom
                      slaughter arranged on the buyer's behalf. Poultry I sell is within my
                      state's on-farm exemption limits.
                    </span>
                  </label>
                )}
                <label className="flex cursor-pointer items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={ackTerms}
                    onChange={(e) => setAckTerms(e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-foreground">
                    I have read and agree to the{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="font-medium text-primary hover:underline"
                    >
                      Founding Maker Terms
                    </a>
                    .
                  </span>
                </label>
              </div>
            </section>
          )}

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setLocation("/seller")}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={!canSubmit || submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save attestation"
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
